<script lang="ts">
export default {
  name: 'ExternalPolicyUploadModal',
};
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { FileSpreadsheet, ScanText, ArrowLeft } from '@lucide/vue';
import AppModal from '@/components/v2/AppModal.vue';
import SpreadsheetUploadPanel from '@/components/v2/external_policy_upload/SpreadsheetUploadPanel.vue';
import BinderUploadPanel from '@/components/v2/external_policy_upload/BinderUploadPanel.vue';
import BinderReviewList from '@/components/v2/binder_review/BinderReviewList.vue';
import { binderReviewService } from '@/services/v2/BinderReviewService';
import { PENDING_BINDER_REVIEW_STATUS } from '@/types/BinderReview';
import { BinderReviewItem } from '@/types/BinderReview';
import { useToast } from '@/composables/useToast';
import { externalPolicyName } from '@/utilities/OrgConfig';

// Uploading is a short, transactional task launched from the policy list, so it stays in
// a modal. Reviewing a scan is not: it runs for minutes and the broker may come back to
// it, so a submitted binder hands off to its own route.
const props = defineProps<{
  show: boolean;
  accountId: string;
  isParent?: boolean;
}>();

const emit = defineEmits<{
  close: [];
  uploaded: [];
}>();

const router = useRouter();
const toast = useToast();

const view = ref<'' | 'spreadsheet' | 'binder'>('');
const pendingSubmissions = ref<BinderReviewItem[]>([]);
const loadingPending = ref(false);

const title = computed(() => `Upload ${externalPolicyName()} Policies`);

function reviewRoute(submissionId: number) {
  return {
    name: 'account-external-policy-binder-review',
    params: { id: props.accountId, submissionId: String(submissionId) },
  };
}

function close() {
  view.value = '';
  emit('close');
}

function openSubmission(submission: { submissionId?: number }) {
  if (!submission.submissionId) {
    return;
  }
  close();
  router.push(reviewRoute(submission.submissionId));
}

function handleSpreadsheetUploaded() {
  toast.success('External policies uploaded.');
  close();
  emit('uploaded');
}

function showPending() {
  view.value = '';
  refreshPending();
}

async function refreshPending() {
  if (props.isParent) {
    return;
  }
  loadingPending.value = true;
  try {
    // This panel is about scans submitted from this modal, so it shows only the
    // externally uploaded ones -- the account's policy binders belong on the policies list.
    const response = await binderReviewService.list(props.accountId, {
      status: PENDING_BINDER_REVIEW_STATUS,
      source: 'external',
    });
    pendingSubmissions.value = response.items;
  } catch {
    pendingSubmissions.value = [];
  } finally {
    loadingPending.value = false;
  }
}

watch(
  () => props.show,
  (isOpen) => {
    if (isOpen) {
      view.value = '';
      refreshPending();
    }
  },
);
</script>

<template>
  <AppModal
    :show="show"
    :title="title"
    size="xl"
    :handle-cancel="close">
    <!-- Each upload panel owns its own submit, so the modal offers only a way out. -->
    <template #actions>
      <button
        class="btn btn-sm"
        data-testid="close-upload-modal"
        @click="close">
        Close
      </button>
    </template>

    <template #content>
      <!-- The title carries the org name, so tests scope to the modal by this instead. -->
      <div class="flex flex-col gap-4" data-testid="external-policy-upload-modal">
        <!-- Step 1: choose upload method -->
        <div
          v-if="!view"
          class="flex flex-col gap-4">
          <div
            class="grid grid-cols-1 gap-4"
            :class="{ 'md:grid-cols-2': !isParent }">
            <button
              type="button"
              class="flex flex-col gap-1 rounded-lg border border-base-300 p-4 text-left transition-shadow hover:shadow-md"
              data-testid="choose-spreadsheet"
              @click="view = 'spreadsheet'">
              <span class="flex items-center gap-2 font-semibold">
                <FileSpreadsheet :size="20" class="text-secondary" /> Upload via spreadsheet
              </span>
              <span class="text-sm text-base-content/60">
                Download our template, fill in your policies, and upload the completed file.
              </span>
            </button>

            <button
              v-if="!isParent"
              type="button"
              class="flex flex-col gap-1 rounded-lg border border-base-300 p-4 text-left transition-shadow hover:shadow-md"
              data-testid="choose-binder"
              @click="view = 'binder'">
              <span class="flex items-center gap-2 font-semibold">
                <ScanText :size="20" class="text-secondary" /> Scan a binder
              </span>
              <span class="text-sm text-base-content/60">
                Upload binder PDFs and we'll extract the policy details automatically.
              </span>
            </button>
          </div>

          <div
            v-if="!isParent && (loadingPending || pendingSubmissions.length > 0)"
            class="rounded-lg border border-base-300 p-3">
            <p class="mb-2 text-sm font-medium">Pending binder scans</p>
            <BinderReviewList
              :items="pendingSubmissions"
              :loading="loadingPending"
              @open="openSubmission" />
          </div>
        </div>

        <!-- Step 2: the chosen upload -->
        <div
          v-else
          class="flex flex-col gap-4">
          <button
            type="button"
            class="btn btn-ghost btn-sm w-fit"
            data-testid="back-to-choices"
            @click="view = ''">
            <ArrowLeft :size="16" /> Upload options
          </button>

          <SpreadsheetUploadPanel
            v-if="view === 'spreadsheet'"
            :account-id="accountId"
            :is-parent="isParent"
            @uploaded="handleSpreadsheetUploaded" />

          <BinderUploadPanel
            v-else
            :account-id="accountId"
            @submitted="openSubmission"
            @finished="showPending" />
        </div>
      </div>
    </template>
  </AppModal>
</template>
